import { useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import {
  FlaggedType,
  MassActionRequest,
  Member,
  MemberStatus,
  ModerationEntityType,
  ModerationItem,
  ModerationOpMode,
  ModerationStatus,
} from '@tribeplatform/gql-client/types'
import { MemberAvatar } from '@tribeplatform/react-components/common/components/member'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useModerationItems,
  useUpdateModerationItem,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button, IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MassActionBanner } from '../../Admin/MassActions/MassActionBanner.js'
import { MemberActionItems } from '../../Member/MemberActionItems.js'
import { EmptyState } from '../EmptyState.js'
import { ModerationItemNote } from '../ModerationItemNote.js'
import { PendingPostsLoading } from '../PendingPosts/PendingPostsLoading.js'
import {
  ReportCategoryDropdown,
  ReportCategoryItem,
} from '../ReportCategoryDropdown.js'

import { MemberRejectedModal } from './MemberRejectedModal.js'

export const ReportedMembers = () => {
  const [selectedReportCategory, setSelectedReportCategory] =
    useState<ReportCategoryItem | null>(null)
  const { $t } = useI18n()

  const {
    data,
    fetchNextPage = false,
    hasNextPage,
    isFetching,
    isLoading,
  } = useModerationItems({
    fields: {
      entity: {
        onMember: {
          profilePicture: 'basic',
          authMemberProps: 'all',
        },
      },
      reporters: {
        variables: { limit: 5 },
        fields: {
          reporter: 'basic',
        },
      },
    },
    variables: {
      limit: 5,
      entityType: ModerationEntityType.MEMBER,
      status: ModerationStatus.REVIEW,
      reportCategories: selectedReportCategory
        ? [selectedReportCategory.value]
        : undefined,
    },
  })

  const [updating, setUpdating] = useState<'accepting' | 'rejecting' | null>(
    null,
  )
  const [memberRejectedModal, setMemberRejectedModal] = useState<{
    action: ModerationOpMode
    moderationItem: ModerationItem
  }>(null)
  const [massActionRequest, setMassActionRequest] =
    useState<MassActionRequest>(null)

  const { mutateAsync: updateModerationItem } = useUpdateModerationItem()

  const acceptMember = async (moderationItemId: string) => {
    setUpdating('accepting')
    try {
      await updateModerationItem({
        id: moderationItemId,
        input: {
          status: ModerationStatus.REJECTED,
        },
      })
      toast({
        title: 'Member has been allowed',
        status: 'success',
      })
    } catch (_) {
      toast({
        title: 'Unable to allow member',
        status: 'error',
      })
    } finally {
      setUpdating(null)
    }
  }

  const { nodes: moderationItems } =
    simplifyPaginatedResult<ModerationItem>(data)

  const shouldShowEmptyState =
    !isLoading && !isFetching && !hasNextPage && moderationItems?.length === 0

  return (
    <>
      <ReportCategoryDropdown
        className="mb-2"
        value={selectedReportCategory}
        onChange={setSelectedReportCategory}
      />
      <InfiniteScroll
        pageStart={0}
        loadMore={fetchNextPage}
        hasMore={hasNextPage || isLoading}
        threshold={800}
        className="flex flex-col space-y-5"
        loader={<PendingPostsLoading />}
      >
        <MassActionBanner massActionRequest={massActionRequest} />
        {shouldShowEmptyState && <EmptyState />}
        {moderationItems?.map(moderationItem => {
          const member = moderationItem?.entity as Member
          return (
            <Card key={moderationItem?.entity?.id}>
              <Card.Content className="flex flex-col space-y-5">
                <ModerationItemNote
                  moderationItem={moderationItem}
                  flaggedBy={FlaggedType.MEMBER}
                />
                <div>
                  <Link
                    href={moderationItem?.entity?.relativeUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <MemberAvatar size="2.5x" member={member} />
                      </div>
                      <div className="ms-4">
                        <div
                          className="text-sm font-medium text-content"
                          translate="no"
                        >
                          {member.name}
                        </div>
                        <div className="text-sm text-content-subdued">
                          {member.tagline}
                        </div>
                      </div>
                      <div className="flex-1 flex justify-end">
                        <Dropdown placement="bottom-end">
                          <Dropdown.ButtonMinimal
                            aria-label={$t({
                              defaultMessage: 'Options',
                              id: 'Generics.Options',
                            })}
                          >
                            <IconButton
                              as="span"
                              variant="tertiaryNeutral"
                              rounded
                              icon={<SvgIcon name="dots-horizontal" />}
                            />
                          </Dropdown.ButtonMinimal>
                          <MemberActionItems
                            member={member}
                            context="moderation"
                          />
                        </Dropdown>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="flex gap-3">
                  <Button
                    className="flex-1"
                    variant="primary"
                    loading={updating === 'accepting'}
                    disabled={updating === 'rejecting'}
                    onClick={() => {
                      acceptMember(moderationItem.id)
                    }}
                  >
                    <T
                      id="Moderation.Action.KeepMember"
                      defaultMessage="Keep"
                    />
                  </Button>
                  <Button
                    className="flex-1"
                    variant="secondaryNeutral"
                    loading={updating === 'accepting'}
                    disabled={
                      updating === 'rejecting' ||
                      (moderationItem?.entity as Member)?.status ===
                        MemberStatus.Suspended
                    }
                    onClick={() =>
                      setMemberRejectedModal({
                        action: ModerationOpMode.Retain,
                        moderationItem,
                      })
                    }
                  >
                    <T
                      id="Moderation.Action.SuspendMember"
                      defaultMessage="Suspend"
                    />
                  </Button>
                  <Button
                    className="flex-1"
                    variant="secondaryNeutral"
                    loading={updating === 'rejecting'}
                    disabled={updating === 'accepting'}
                    onClick={() =>
                      setMemberRejectedModal({
                        action: ModerationOpMode.Purge,
                        moderationItem,
                      })
                    }
                  >
                    <T
                      id="Moderation.Action.DeleteMember"
                      defaultMessage="Remove"
                    />
                  </Button>
                </div>
              </Card.Content>
            </Card>
          )
        })}
        {!!memberRejectedModal && (
          <MemberRejectedModal
            open={!!memberRejectedModal}
            onClose={() => setMemberRejectedModal(null)}
            moderationItem={memberRejectedModal.moderationItem}
            action={memberRejectedModal.action}
            setMassActionRequest={setMassActionRequest}
          />
        )}
      </InfiniteScroll>
    </>
  )
}
